import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  catalogItemResponseSchema,
  catalogResponseSchema,
  catalogUnitSchema,
  type CatalogItem,
  type CreateCatalogItemRequest,
} from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPatch, apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';

const input =
  'w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';
const primary =
  'rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';
const secondary =
  'rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100 disabled:opacity-40';

const EMPTY: CreateCatalogItemRequest = {
  nameHe: '',
  nameEn: '',
  category: '',
  ownership: 'company',
  tracking: 'serialized',
  controlled: false,
  consumable: false,
  unit: 'pcs',
  requiresSignature: true,
  returnable: true,
  transferable: true,
  defaultDueDays: null,
};

/**
 * Catalog management (REQUIREMENTS §2.6). §3.1 is answered "the catalog is
 * data": this is where the unit's real list gets typed in, one kind of item
 * at a time, by an admin or csm.
 */
export function CatalogPage() {
  const { t } = useTranslation();
  const state = useApi('/catalog', catalogResponseSchema);
  const [editing, setEditing] = useState<CatalogItem | 'new' | null>(null);

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">{t('catalog.title')}</h2>
        <button type="button" onClick={() => setEditing('new')} className={primary} data-testid="catalog-add">
          {t('catalog.add')}
        </button>
      </div>

      {editing && (
        <div className="mt-4">
          <CatalogItemForm
            item={editing === 'new' ? null : editing}
            onDone={() => {
              setEditing(null);
              state.reload();
            }}
          />
        </div>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="catalog-table">
          <thead>
            <tr className="border-b border-slate-200 text-xs text-slate-500">
              <th className="py-2 text-start font-medium">{t('catalog.columns.nameHe')}</th>
              <th className="py-2 text-start font-medium">{t('catalog.columns.nameEn')}</th>
              <th className="py-2 text-start font-medium">{t('catalog.columns.category')}</th>
              <th className="py-2 text-start font-medium">{t('catalog.columns.kind')}</th>
              <th className="py-2 text-start font-medium">{t('catalog.columns.flags')}</th>
              <th className="py-2 text-start font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {state.data.items.map((item) => (
              <tr key={item.id} className={item.active ? '' : 'opacity-60'} data-testid={`catalog-${item.id}`}>
                <td className="py-2 font-medium text-slate-900">{item.nameHe}</td>
                <td className="py-2 text-slate-700">
                  <span className="latin-inline">{item.nameEn}</span>
                </td>
                <td className="py-2 text-slate-700">{item.category}</td>
                <td className="py-2 text-slate-700">
                  {t(`inventory.ownership.${item.ownership}`)} · {t(`inventory.tracking.${item.tracking}`)}
                </td>
                <td className="py-2 text-xs text-slate-600">
                  {[
                    item.controlled && t('catalog.flags.controlled'),
                    item.consumable && t('catalog.flags.consumable'),
                    item.requiresSignature && t('catalog.flags.requiresSignature'),
                    item.returnable && t('catalog.flags.returnable'),
                    !item.transferable && t('catalog.flags.notTransferable'),
                    !item.active && t('org.inactive'),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </td>
                <td className="py-2">
                  <button type="button" onClick={() => setEditing(item)} className="text-xs text-slate-500 underline-offset-4 hover:underline" data-testid={`catalog-edit-${item.id}`}>
                    {t('org.edit')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CatalogItemForm({ item, onDone }: { item: CatalogItem | null; onDone: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateCatalogItemRequest & { active: boolean }>(
    item ? { ...item } : { ...EMPTY, active: true },
  );
  const save = useAction(async () => {
    if (item) {
      const { ownership: _o, tracking: _t, ...patch } = form;
      return apiPatch(`/catalog/${item.id}`, catalogItemResponseSchema, patch);
    }
    const { active: _a, ...body } = form;
    return apiPost('/catalog', catalogItemResponseSchema, body);
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await save.run()) onDone();
  };

  const check = (key: 'controlled' | 'consumable' | 'requiresSignature' | 'returnable' | 'transferable' | 'active') => (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={form[key]} onChange={(e) => set(key, e.target.checked)} data-testid={`catalog-${key}`} />
      {t(`catalog.flags.${key}`)}
    </label>
  );

  return (
    <form onSubmit={(e) => void submit(e)} className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-testid="catalog-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          {t('catalog.columns.nameHe')}
          <input className={input} value={form.nameHe} onChange={(e) => set('nameHe', e.target.value)} required data-testid="catalog-nameHe" />
        </label>
        <label className="text-sm text-slate-600">
          {t('catalog.columns.nameEn')}
          <input className={`${input} latin-inline`} value={form.nameEn} onChange={(e) => set('nameEn', e.target.value)} required data-testid="catalog-nameEn" />
        </label>
        <label className="text-sm text-slate-600">
          {t('catalog.columns.category')}
          <input className={input} value={form.category} onChange={(e) => set('category', e.target.value)} required data-testid="catalog-category" />
        </label>
        <label className="text-sm text-slate-600">
          {t('catalog.unit')}
          <select className={input} value={form.unit} onChange={(e) => set('unit', e.target.value as typeof form.unit)}>
            {catalogUnitSchema.options.map((u) => (
              <option key={u} value={u}>{t(`catalog.units.${u}`)}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-slate-600">
          {t('catalog.ownership')}
          <select className={input} value={form.ownership} disabled={!!item} onChange={(e) => set('ownership', e.target.value as typeof form.ownership)} data-testid="catalog-ownership">
            <option value="company">{t('inventory.ownership.company')}</option>
            <option value="personal">{t('inventory.ownership.personal')}</option>
          </select>
        </label>
        <label className="text-sm text-slate-600">
          {t('catalog.tracking')}
          <select className={input} value={form.tracking} disabled={!!item} onChange={(e) => set('tracking', e.target.value as typeof form.tracking)} data-testid="catalog-tracking">
            <option value="serialized">{t('inventory.tracking.serialized')}</option>
            <option value="quantity">{t('inventory.tracking.quantity')}</option>
          </select>
        </label>
        <label className="text-sm text-slate-600">
          {t('catalog.defaultDueDays')}
          <input className={`${input} latin-inline`} inputMode="numeric" value={form.defaultDueDays ?? ''} onChange={(e) => set('defaultDueDays', e.target.value === '' ? null : Number(e.target.value))} />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap gap-4">
        {check('controlled')}
        {check('consumable')}
        {check('requiresSignature')}
        {check('returnable')}
        {check('transferable')}
        {item && check('active')}
      </div>
      {item && <p className="mt-2 text-xs text-slate-500">{t('catalog.lockedHint')}</p>}
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={save.busy} className={primary} data-testid="catalog-save">{t('org.save')}</button>
        <button type="button" onClick={onDone} className={secondary}>{t('org.cancel')}</button>
      </div>
      <div className="mt-2"><ActionError error={save.error} /></div>
    </form>
  );
}
